﻿import React, { useState, cloneElement, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  IconButton,
  Popover,
  Box,
  Grid,
  Typography,
  ButtonBase,
  useMediaQuery,
  Tooltip,
  Slide,
} from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { GetSoftware } from '../../api/software';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import FASTLink from '../../components/FASTLogineApp/FASTLink';
import SSILink from '../../components/SSILink/SSILink';
import SharepointLink from '../Global/SharepointLink/SharepointLink';
import Skeleton from '@mui/material/Skeleton';
import { headerIconButtonStyles } from '../Navigation/Components/StyledComponents';

// Reusable Popover Component
const NavDrawer = ({
  triggerIcon = <AppsIcon />,
  items = [],
  columns = 3,
  fallbackIcon = LaunchOutlinedIcon,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  let routePrefix = 'Software';

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Define color palette for fallback icons only
  const fallbackColors = [
    'rgba(0, 117, 191, 0.15)', // blue
    'rgba(0, 150, 136, 0.15)', // teal
    'rgba(255, 193, 7, 0.15)', // yellow
  ];

  const handleItemClick = (item, event) => {
    // Handle different action types
    if (item.onClick) {
      // Custom action
      event.preventDefault();
      item.onClick();
    }
    // For linkType 1, let the component handle navigation (Link or <a>)

    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'nav-drawer' : undefined;

  return (
    <>
      <IconButton
        aria-describedby={id}
        aria-label="Applications"
        onClick={handleClick}
        sx={{
          ...headerIconButtonStyles,
          '& .MuiSvgIcon-root': {
            fontSize: isMobile ? '1.5rem' : '1.25rem',
          },
        }}
      >
        {triggerIcon}
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: isMobile ? 'center' : 'bottom',
          horizontal: isMobile ? 'left' : 'left',
        }}
        transformOrigin={{
          vertical: isMobile ? 'center' : 'top',
          horizontal: isMobile ? 'left' : 'right',
        }}
        TransitionComponent={isMobile ? Slide : undefined}
        TransitionProps={{
          timeout: 350,
          ...(isMobile && { direction: 'right' }),
        }}
        slotProps={{
          paper: {
            sx: {
              ...(!isMobile && {
                width: 280,
                maxWidth: 320,
                maxHeight: '97vh',
                borderRadius: 2,
              }),
              ...(isMobile && {
                width: '100vw',
                maxWidth: '100vw',
                height: '100vh',
                maxHeight: '97vh',
                borderRadius: 0,
                position: 'fixed !important',
                left: '0 !important',
                top: '0 !important',
                right: 'auto !important',
                bottom: 'auto !important',
                boxShadow: 'none',
              }),
              margin: 0,
            },
          },
        }}
      >
        <Box
          sx={{
            p: 1,
            maxHeight: isMobile ? 'calc(97vh - 60px)' : 'calc(55vh - 60px)',
            height: isMobile ? '100%' : 'auto',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              py: 0.5,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              My Tools
            </Typography>
            {isMobile && (
              <IconButton
                onClick={handleClose}
                size="large"
                sx={{
                  ml: 1,
                  '& .MuiSvgIcon-root': {
                    fontSize: '2rem',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
          <Grid container spacing={0.5}>
            {items.map((item, index) => {
              const colorIndex = index % columns;
              const bgColor = fallbackColors[colorIndex];
              // Check if this item is using the fallback icon
              const isFallback = item.isFallback;

              // Check if it's a route (starts with / and no file extension or TLD)
              const isRoute =
                item.href?.startsWith('/') &&
                !/\.(pdf|docx?|xlsx?|csv|zip|txt|pptx?|com|org|net|edu|gov|io|co)$/i.test(
                  item.href
                );

              // Determine component type
              let buttonComponent = 'div';
              let linkProps = {};

              // Check if a custom component is provided
              if (item.component) {
                // Use custom component provided in the item
                buttonComponent = item.component;
                linkProps = {
                  ...(item.componentProps || {}),
                  closePopover: handleClose, // Pass close function to custom components
                  onMouseEnter: () => setHoveredIndex(index), // Pass hover handlers
                  onMouseLeave: () => setHoveredIndex(null),
                };
              } else if (isRoute) {
                buttonComponent = Link;
                linkProps = { to: item.href };
              } else {
                buttonComponent = SharepointLink;
                linkProps = {
                  baseUrl: `${process.env.REACT_APP_AGENTPORTAL_PREFIX}/${routePrefix}`,
                  linkType: item.linkType,
                  link: item.href,
                };
              }

              return (
                <Grid key={index} size={Math.floor(12 / columns)}>
                  <Tooltip
                    title={item.tooltip || item.label}
                    arrow
                    placement="top"
                    slotProps={{
                      popper: {
                        sx: { zIndex: 1500 },
                        container: document.body,
                        modifiers: [
                          {
                            name: 'preventOverflow',
                            options: {
                              boundary: 'viewport',
                            },
                          },
                        ],
                      },
                      tooltip: {
                        sx: {
                          maxWidth: 200,
                          fontSize: '0.875rem',
                          padding: '8px 12px',
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                          bgcolor: 'rgba(97, 97, 97, 0.95)',
                        },
                      },
                      arrow: {
                        sx: {
                          color: 'rgba(97, 97, 97, 0.95)',
                        },
                      },
                    }}
                  >
                    <span style={{ display: 'inline-flex', width: '100%' }}>
                      <ButtonBase
                        component={buttonComponent}
                        {...linkProps}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={(e) => handleItemClick(item, e)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 1,
                          borderRadius: 1,
                          width: '100%',
                          minHeight: { xs: 70, sm: 80 },
                          textDecoration: 'none',
                          color: 'inherit',
                          transition: isMobile ? 'none' : 'min-height 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            minHeight: isMobile ? { xs: 70 } : { xs: 85, sm: 95 },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 40, sm: 48 },
                            height: { xs: 40, sm: 48 },
                            borderRadius: isFallback ? '50%' : 0,
                            backgroundColor: isFallback ? bgColor : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 0.5,
                            color: 'primary.main',
                          }}
                        >
                          {cloneElement(item.icon, { sx: { fontSize: { xs: 24, sm: 28 } } })}
                        </Box>
                        <Typography
                          variant="caption"
                          align="center"
                          sx={{
                            maxWidth: { xs: 60, sm: 70 },
                            overflow: 'hidden',
                            textOverflow: isMobile ? 'clip' : 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: isMobile
                              ? 'unset'
                              : hoveredIndex === index
                                ? 'unset'
                                : 2,
                            WebkitBoxOrient: 'vertical',
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            lineHeight: 1.2,
                            transition: isMobile ? 'none' : 'all 0.2s ease',
                            whiteSpace: isMobile
                              ? 'normal'
                              : hoveredIndex === index
                                ? 'normal'
                                : undefined,
                          }}
                        >
                          {item.label}
                        </Typography>
                      </ButtonBase>
                    </span>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Popover>
    </>
  );
};

export const ApplicationsNavMenu = () => {
  const GetSvgIcon = ({ fileName, alt = 'icon' }) => {
    const [failed, setFailed] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    // If no fileName or it's blank, return fallback immediately
    if (failed || !fileName || fileName.trim() === '') {
      return (
        <Box
          sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 117, 191, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 0.5,
            color: 'primary.main',
          }}
        >
          <LaunchOutlinedIcon
            sx={{
              borderRadius: '50%',
              backgroundColor: 'transparent',
            }}
          />
        </Box>
      );
    }

    return (
      <>
        {!imageLoaded && (
          <Skeleton
            variant="rounded"
            sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}
          />
        )}
        <img
          src={'/CmsImages/' + fileName}
          alt={alt}
          onError={() => setFailed(true)}
          onLoad={() => setImageLoaded(true)}
          style={{ display: imageLoaded ? 'block' : 'none', width: '48px', height: 'auto' }}
        />
      </>
    );
  };

  const getApps = useQuery({ queryKey: ['GetApps'], queryFn: () => GetSoftware(false)() });

  useEffect(() => {
    if (getApps.data != null) {
      setAppData(JSON.parse(getApps.data));
    }
  }, [getApps.data]);

  const [appData, setAppData] = useState([]);

  // Define component mapping
  const componentMap = {
    'iProvide INSpeed Plus eApp': FASTLink,
    'IQumulate eApplication': FASTLink,
    'Secure Solutions Illustrator (Web Version)': SSILink,
    'Annuities, Generation Legacy, Lifetime Navigator - ULSG, Term, Whole Life': SSILink,
  };

  const btnStyle = {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  };

  // Props mapping (optional - if you need specific props per title)
  const componentPropsMap = {
    'iProvide INSpeed Plus eApp': {
      id: 'FAST' + Math.floor(Math.random() * 10),
      customStyle: btnStyle,
    },
    'IQumulate eApplication': {
      id: 'FAST' + Math.floor(Math.random() * 10),
      customStyle: btnStyle,
    },
    'Secure Solutions Illustrator (Web Version)': {
      id: 'SSI',
      label: 'Secure Solutions Illustrator (SSI)',
      customStyle: btnStyle,
    },
    'Annuities, Generation Legacy, Lifetime Navigator - ULSG, Term, Whole Life': {
      id: 'Annuities',
      label: 'Annuities, Generation Legacy, Lifetime Navigator - ULSG, Term, Whole Life',
      customStyle: btnStyle,
    },
  };

  const actionItems =
    appData?.map((item) => ({
      icon: item.ImageName ? (
        <GetSvgIcon fileName={item.ImageName} alt={item.Title} />
      ) : (
        <LaunchOutlinedIcon />
      ), // Fallback if no ImageName
      label: item.Title,
      href: item.Url,
      linkType: item.LinkTypeID,
      tooltip: item.Tooltip,
      isFallback: !item.ImageName, // Show colored circle if no custom icon
      ...(componentMap[item.Title] && {
        component: componentMap[item.Title],
        componentProps: {
          to: item.Url,
          ...componentPropsMap[item.Title], // Merge any title-specific props
        },
      }),
    })) || [];

  if (getApps.isLoading || getApps.isFetching) {
    return <div style={{ cursor: 'wait' }}></div>;
  }

  if (getApps.error) {
    return (
      <div style={{ cursor: 'default' }}>
        <ErrorOutlinedIcon color="error" sx={{ fontSize: 32 }} />
      </div>
    );
  }

  return (
    <div style={{ cursor: 'default' }}>
      <NavDrawer items={actionItems} columns={3} />
    </div>
  );
};
